import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShareNetwork, Users, Globe, Warning, CaretRight } from '@phosphor-icons/react';
import axios from 'axios';
import { shareApi } from './shareApi';
import ShareDialog from './ShareDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** 社内に共有 — 人や部署を選んで、アプリ内で知らせる（パスワード不要）。 */
const InternalShare = ({ documentIds, bundleId, onClose, isAdmin }) => {
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [dept, setDept] = useState('');
  const [message, setMessage] = useState('');
  const [blocked, setBlocked] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [notifyApp, setNotifyApp] = useState(true);
  const [mail, setMail] = useState(null);     // {paused, reason, queued}
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    // 全員が使えるのは chat の名簿（/api/users は管理者専用）
    axios.get(`${API}/chat/directory`).then((r) => setUsers(r.data || []))
      .catch(() => setUsers([]));
    axios.get(`${API}/dms/internal-shares/mail-queue`).then((r) => setMail(r.data))
      .catch(() => setMail(null));
  }, []);

  const submit = async () => {
    if (!picked.length && !dept) { toast.error('共有する相手を選んでください'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/dms/internal-shares`, {
        document_ids: documentIds, bundle_id: bundleId,
        user_ids: picked, department_id: dept || null, message,
        send_email: sendEmail, notify_app: notifyApp,
      }).then((x) => x.data);
      setBlocked(r.blocked || []);
      setDone(r);
      const q = r.mail?.queued || 0;
      toast.success(q ? `${r.shared_with}人に共有しました（メール${q}件は送信再開後に送ります）`
        : `${r.shared_with}人に共有しました`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '共有できませんでした');
    } finally { setBusy(false); }
  };

  const grant = async (folderId, userIds) => {
    try {
      await axios.post(`${API}/dms/internal-shares/grant-view`,
        { folder_id: folderId, user_ids: userIds });
      toast.success('閲覧を許可しました');
      setBlocked([]);
    } catch (e) { toast.error(e?.response?.data?.detail || '許可できませんでした'); }
  };

  return (
    <div className="space-y-3" data-testid="internal-share-body">
      <div className="max-h-48 overflow-y-auto rounded border border-zinc-200 divide-y divide-zinc-100">
        {users.map((u) => (
          <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 text-[12px] text-zinc-700">
            <input type="checkbox" checked={picked.includes(u.id)}
              data-testid={`internal-share-user-${u.id}`}
              onChange={() => setPicked((p) => p.includes(u.id) ? p.filter((x) => x !== u.id) : [...p, u.id])}
              className="accent-teal-700" />
            <span className="truncate">{u.name || u.email}</span>
            <span className="ml-auto text-[10px] text-zinc-400">{u.role}</span>
          </label>
        ))}
        {users.length === 0 && <p className="px-2 py-3 text-[11px] text-zinc-400">利用者を読み込めませんでした</p>}
      </div>
      <label className="flex items-start gap-2 text-[11px] text-zinc-600">
        <input type="checkbox" checked={sendEmail} data-testid="internal-share-email"
          onChange={(e) => setSendEmail(e.target.checked)} className="mt-0.5 accent-teal-700" />
        <span>
          メールで知らせる（相手のアカウントのメールアドレス宛・ファイルは添付しません）
          {mail?.paused && (
            <span className="block text-amber-700" data-testid="internal-share-mail-paused">
              メール送信停止中のため、送信は再開後に行われます
              {mail.queued ? `（送信待ち ${mail.queued}件）` : ''}
            </span>
          )}
        </span>
      </label>
      <label className="flex items-center gap-2 text-[11px] text-zinc-600">
        <input type="checkbox" checked={notifyApp} data-testid="internal-share-notify-app"
          onChange={(e) => setNotifyApp(e.target.checked)} className="accent-teal-700" />
        チャットと作業トレイでも知らせる
      </label>
      <button type="button" data-testid="internal-share-copy-link"
        onClick={async () => {
          const path = bundleId ? `/documents?bundle=${bundleId}` : `/documents?open=${documentIds[0]}`;
          try {
            await navigator.clipboard.writeText(`${window.location.origin}${path}`);
            toast.success('社内リンクをコピーしました（通知は送っていません）');
          } catch { toast.error('コピーできませんでした'); }
        }}
        className="text-[11px] text-teal-700 underline">リンクをコピー（通知なし）</button>
      <input value={message} onChange={(e) => setMessage(e.target.value)}
        data-testid="internal-share-message" placeholder="ひとこと（任意）"
        className="w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px] outline-none focus:border-teal-500" />

      {blocked.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 space-y-1"
          data-testid="internal-share-blocked">
          <p className="flex items-center gap-1 text-[11px] font-semibold text-amber-900">
            <Warning size={12} weight="fill" />権限が無く、見られない人がいます
          </p>
          {blocked.map((b) => (
            <div key={b.user_id} className="text-[11px] text-amber-900">
              {b.user_name}: {b.documents.map((d) => d.title).join('、')}
              {isAdmin && (
                <button type="button" data-testid={`internal-share-grant-${b.user_id}`}
                  onClick={() => grant(b.documents[0].folder_id, [b.user_id])}
                  className="ml-2 underline text-[10px]">閲覧を許可する</button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} data-testid="internal-share-close"
          className="px-3 py-1.5 rounded border border-zinc-300 text-[12px] text-zinc-600">
          {done ? '閉じる' : 'やめる'}
        </button>
        {!done && (
          <button type="button" onClick={submit} disabled={busy} data-testid="internal-share-submit"
            className="px-3 py-1.5 rounded bg-teal-700 text-white text-[12px] font-semibold disabled:opacity-40">
            {busy ? '共有中…' : '社内に共有する'}
          </button>
        )}
      </div>
    </div>
  );
};

/** 「共有」ひとつで、社内と社外のどちらかを選ぶ。 */
export default function ShareMenu({ open, onClose, documentIds = [], bundleId = null,
  label = '', isAdmin = false }) {
  const [mode, setMode] = useState(null);   // null | 'internal' | 'external'
  useEffect(() => { if (!open) setMode(null); }, [open]);
  if (!open) return null;
  if (mode === 'external') {
    return <ShareDialog open onClose={onClose} documentIds={documentIds}
      bundleId={bundleId} label={label} isAdmin={isAdmin} />;
  }
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4"
      data-testid="dms-share-menu" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <ShareNetwork size={18} weight="duotone" className="text-teal-700" />
          <h2 className="text-base font-bold text-zinc-900">共有</h2>
          <span className="text-[11px] text-zinc-400">
            {bundleId ? `バインダー${label ? ` — ${label}` : ''}` : `${documentIds.length}件の書類`}
          </span>
        </div>

        {mode === 'internal' ? (
          <InternalShare documentIds={documentIds} bundleId={bundleId}
            isAdmin={isAdmin} onClose={onClose} />
        ) : (
          <>
            <button type="button" onClick={() => setMode('internal')} data-testid="share-choose-internal"
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-zinc-200 hover:border-teal-500 text-left">
              <Users size={18} weight="duotone" className="text-teal-700" />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-zinc-900">社内に共有</span>
                <span className="block text-[11px] text-zinc-500">相手の画面に通知が届きます。パスワード不要。</span>
              </span>
              <CaretRight size={14} className="ml-auto text-zinc-300" />
            </button>
            <button type="button" onClick={() => setMode('external')} data-testid="share-choose-external"
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-zinc-200 hover:border-teal-500 text-left">
              <Globe size={18} weight="duotone" className="text-sky-700" />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-zinc-900">社外に共有（パスワード付き）</span>
                <span className="block text-[11px] text-zinc-500">お客様向け。期限付きリンクとパスワードを作ります。</span>
              </span>
              <CaretRight size={14} className="ml-auto text-zinc-300" />
            </button>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} data-testid="share-menu-close"
                className="px-3 py-1.5 rounded border border-zinc-300 text-[12px] text-zinc-600">やめる</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { shareApi };
